import {
  browserPdfOutput,
  type ProvisionAlertList,
  type ProvisionCategory,
  type ProvisionDashboard,
  type ProvisionItemList,
  type ProvisionLibraryInfo,
  type ProvisionLibraryReport,
  type ProvisionLot,
  type ProvisionMovement,
  type ProvisionSettings,
  type ProvisionSource,
  type VictuallingData,
  type VictuallingFleetData,
} from '@felix/ui';
import { api, officeApi } from './api.js';

/** Sorgu dizesi: boş, `undefined` ve `false` yazılmaz. */
function query(values: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

const vessel = (vesselId: string | null) => (vesselId ? { vesselId } : {});
const CLOSED = new Set(['closed', 'cancelled']);

/**
 * Ofiste kumanya (ADR 0099): kumanya stoğu gemi verisidir; ofis `provisions.view` yetkili olduğu
 * gemilerinkini salt okunur görür (yazma yok, mimari belge §8). Başlangıç kütüphanesi ve son
 * kullanma uyarı penceresi şirket verisidir, ofiste yönetilir.
 */
export function officeProvisionsSource(): ProvisionSource {
  return {
    scope: 'web',
    dashboard: (vesselId) =>
      api<ProvisionDashboard>(`/office/provisions/dashboard${query(vessel(vesselId))}`),
    categories: (vesselId) =>
      api<ProvisionCategory[]>(`/office/provisions/categories${query(vessel(vesselId))}`),
    items: (q, vesselId) =>
      api<ProvisionItemList>(`/office/provisions/items${query({ ...q, ...vessel(vesselId) })}`),
    lots: (filter, vesselId) =>
      api<ProvisionLot[]>(`/office/provisions/lots${query({ ...filter, ...vessel(vesselId) })}`),
    alerts: (vesselId) =>
      api<ProvisionAlertList>(`/office/provisions/alerts${query(vessel(vesselId))}`),
    movements: (options, vesselId) =>
      api<ProvisionMovement[]>(
        `/office/provisions/movements${query({ ...options, ...vessel(vesselId) })}`,
      ),
    requests: async (vesselId) =>
      (await officeApi.requisitions()).items
        .filter(
          (r) => r.vesselId === vesselId && r.department === 'Provisions' && !CLOSED.has(r.status),
        )
        .map((r) => ({
          id: r.id,
          requisitionNo: r.requisitionNo,
          requestDate: r.requestDate,
          requiredDate: r.requiredDate,
          status: r.status,
          lineCount: r.lineCount,
        })),
    // Aylık iaşe raporu (ADR 0123): seçilen gemi ya da filo; PDF tarayıcıda üretilir
    victualling: (month, vesselId) =>
      api<VictuallingData>(
        `/office/provisions/victualling${query({ month, ...vessel(vesselId) })}`,
      ),
    victuallingFleet: (month) =>
      api<VictuallingFleetData>(`/office/provisions/victualling/fleet${query({ month })}`),
    pdf: browserPdfOutput(),
    brand: async () => {
      try {
        const { branding } = await officeApi.branding();
        return branding?.name ? { name: branding.name, logo: branding.logo ?? null } : null;
      } catch {
        return null;
      }
    },
    office: {
      library: () => api<ProvisionLibraryInfo>('/office/provisions/library'),
      applyLibrary: (dryRun) =>
        api<ProvisionLibraryReport>('/office/provisions/library/apply', {
          method: 'POST',
          body: { dryRun },
        }),
      settings: () => api<ProvisionSettings>('/office/provisions/settings'),
      saveSettings: (input) =>
        api<ProvisionSettings>('/office/provisions/settings', { method: 'PUT', body: input }),
    },
  };
}
